import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Flame, Loader2, Gift, Copy, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { tr } from "@/data/i18n";

export default function Pricing() {
  const { lang, user, subscription } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (user) api.referral().then(({ data }) => setRef(data)).catch(() => {});
  }, [user]);

  const copyRef = () => {
    const link = `${window.location.origin}/?ref=${ref.code}`;
    navigator.clipboard.writeText(link);
    toast.success(tr(lang, "link_copied"));
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/pricing")}`;
  };

  const subscribe = async (lookup_key) => {
    if (!user) { login(); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.checkout(lookup_key, window.location.origin);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setBusy("");
    }
  };

  const plans = [
    { key: "pulsar_monthly", price: "€3,99", per: tr(lang, "per_month"), name: tr(lang, "monthly"),
      features: [tr(lang, "mode_ai_title"), tr(lang, "beginner_mode"), tr(lang, "export_share"), tr(lang, "evolve")] },
    { key: "pulsar_yearly", price: "€39,99", per: tr(lang, "per_year"), name: tr(lang, "yearly"), best: true,
      features: [tr(lang, "mode_ai_title"), tr(lang, "beginner_mode"), tr(lang, "export_share"), tr(lang, "evolve"), "2 " + tr(lang, "monthly") + " gratis"] },
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14">
      <div className="text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-red-400">
          <Flame size={14} /> {tr(lang, "trial_free")}
        </div>
        <h1 className="mt-5 font-display text-4xl font-extrabold uppercase text-white sm:text-5xl">{tr(lang, "pricing_title")}</h1>
        <p className="mt-3 text-zinc-400">{tr(lang, "pricing_sub")}</p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2">
        {plans.map((p, i) => (
          <motion.div key={p.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            className={`relative rounded-3xl border p-8 ${p.best ? "border-red-500/50 bg-gradient-to-br from-red-500/10 to-zinc-950 red-glow" : "border-zinc-800 bg-zinc-900/50"}`}>
            {p.best && <span className="absolute -top-3 left-8 rounded-full bg-red-500 px-3 py-1 text-xs font-bold uppercase text-white">{tr(lang, "best_value")}</span>}
            <h3 className="font-display text-xl font-bold uppercase text-white">{p.name}</h3>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-display text-5xl font-extrabold text-white">{p.price}</span>
              <span className="mb-1 text-zinc-500">{p.per}</span>
            </div>
            <ul className="mt-6 space-y-3">
              {p.features.map((f, k) => (
                <li key={k} className="flex items-center gap-2 text-sm text-zinc-300">
                  <Check size={16} className="text-red-500" /> {f}
                </li>
              ))}
            </ul>
            {subscription?.subscribed ? (
              <div className="mt-8 rounded-full bg-emerald-500/15 py-3 text-center text-sm font-semibold text-emerald-400">✓ {tr(lang, "current")}</div>
            ) : (
              <button onClick={() => subscribe(p.key)} disabled={busy === p.key} data-testid={`subscribe-${p.key}`}
                      className={`mt-8 flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-bold transition-all disabled:opacity-60 ${
                        p.best ? "bg-red-500 text-white hover:bg-red-600" : "border border-zinc-700 text-white hover:border-red-500/40"}`}>
                {busy === p.key ? <Loader2 className="animate-spin" size={18} /> : tr(lang, "subscribe")}
              </button>
            )}
          </motion.div>
        ))}
      </div>
      <p className="mt-8 text-center text-xs text-zinc-600">Test: usa la carta 4242 4242 4242 4242, scadenza futura, qualsiasi CVC.</p>

      {/* Referral */}
      {user && ref && (
        <div className="mt-12 overflow-hidden rounded-3xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-zinc-950 p-8" data-testid="referral-card">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-orange-500"><Gift className="text-white" /></div>
            <div className="flex-1">
              <h3 className="font-display text-2xl font-bold uppercase text-white">{tr(lang, "referral_title")}</h3>
              <p className="mt-1 text-sm text-zinc-400">{tr(lang, "referral_sub")}</p>
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-orange-500/15 px-3 py-1 text-xs font-bold text-orange-300">
                🎁 {tr(lang, "referral_reward")}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 font-mono text-sm text-white">
                  <span className="text-zinc-500">/?ref=</span>{ref.code}
                </div>
                <button onClick={copyRef} data-testid="copy-referral-btn"
                        className="inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-3 text-sm font-bold text-white hover:bg-red-600">
                  <Copy size={16} /> {tr(lang, "copy_link")}
                </button>
                <div className="inline-flex items-center gap-2 text-sm text-zinc-400">
                  <Users size={16} className="text-orange-400" /> {tr(lang, "invite_count")}: <b className="text-white">{ref.count}</b>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
